import React, {memo, useCallback, useMemo} from 'react'
import { Button, Container, Divider, Grid, TextField, Stack, MenuItem, Select, FormControl, InputLabel, SelectChangeEvent, Autocomplete } from '@mui/material'
import dayjs, {Dayjs} from 'dayjs'

import {IBrokerRecordsActions, IBrokerRecordsParams} from '../atom/inputs/brokerRecordTypes'
import AutoCompleteSelect from '../atom/AutoCompleteSelect'
import {BROKER_EXECUTION_STATUSES, BROKER_STATUSES, BOOLEANOPTIONS} from '@meprism/shared/src/utils/constants'
import CustomDatePicker from '../atom/inputs/DatePicker'
import {exportBrokerRecords} from '../../../redux/brokerService'
import {useAppDispatch, useAppSelector} from '../../../redux/storeExports'
import RetriggerScansPopup from './RetriggerScansPopup'
import RetriggerRemovalsPopup from './RetriggerRemovalsPopup'
import RetriggerInviteEmail from './RetriggerInviteEmail'
import AddBrokerRecordPopup from "./AddBrokerRecordPopup";
import { setRecordType } from '../../../redux/RecordTypes/RecordTypeSlice';
import { RecordTypeEnum } from '../../../redux/RecordTypes/RecordTypeTypes';

export interface BrokerOption {
	title: string
	id: string
}

interface FilterContainerProps {
	brokerRecordsReqParams: IBrokerRecordsParams
	dataBrokerOptions: BrokerOption[]
	handleRefetch: () => void
	dispatchBrokerRecordsReqParams: React.Dispatch<IBrokerRecordsActions>
	disableBroker: boolean
	shouldDisplayRecordType?: boolean
}

const FilterContainer = ({
													 dataBrokerOptions,
													 handleRefetch,
													 brokerRecordsReqParams,
													 dispatchBrokerRecordsReqParams,
													 disableBroker,
													 shouldDisplayRecordType = false
												 }: FilterContainerProps) => {
	const {filters} = brokerRecordsReqParams
	const appDispatch = useAppDispatch()
	const startDate = useMemo(() => dayjs(filters.startDate), [filters.startDate])
	const endDate = useMemo(() => dayjs(filters.endDate), [filters.endDate])
	const minEndDate = useMemo(() => dayjs(filters.startDate) || dayjs(), [filters.startDate])
	const selectedRecordType = useAppSelector((state) => state.recordType.selected);

	const handleFilterChange = useCallback(
		(filterKey: string, value: string | any[] | Dayjs | null | boolean) => {
			dispatchBrokerRecordsReqParams({
				type: 'UPDATE_FILTERS',
				payload: {
					[filterKey]: value
				}
			})
		},
		[dispatchBrokerRecordsReqParams]
	)

	const handleRecordTypeChange = useCallback(
		(e: SelectChangeEvent<string>) => {
			const recordType = e.target.value as RecordTypeEnum;
			appDispatch(setRecordType(recordType));
			handleFilterChange('recordType', recordType);
		},
		[appDispatch, handleFilterChange]
	);

	const handleExport = useCallback(
		() => appDispatch(exportBrokerRecords(brokerRecordsReqParams)),
		[appDispatch, brokerRecordsReqParams]
	)

	return (
		<Container>
			<Grid container spacing={4} justifyContent="center" alignItems="center">
				<Grid item lg={4} md={6} xs={12}>
					<TextField
						value={filters.muid}
						onChange={(e) => handleFilterChange('muid', e.target.value)}
						label="Search MUID"
						size="small"
						fullWidth
					/>
				</Grid>
				<Grid item lg={4} md={6} xs={12}>
					<AutoCompleteSelect
						options={dataBrokerOptions}
						onChange={(values) => handleFilterChange('brokerNames', values)}
						label="Data Broker"
						value={filters.brokerNames}
						disabled={disableBroker}
					/>
				</Grid>
				<Grid item lg={4} md={6} xs={12}>
					<AutoCompleteSelect
						options={BROKER_EXECUTION_STATUSES}
						onChange={(values) => handleFilterChange('executionStatuses', values)}
						value={filters.executionStatuses}
						label="Execution Status"
					/>
				</Grid>
				<Grid item lg={4} md={6} xs={12}>
					<AutoCompleteSelect
						options={BROKER_STATUSES}
						onChange={(values) => handleFilterChange('statuses', values)}
						label="Status"
						value={filters.statuses}
					/>
				</Grid>
				{
				shouldDisplayRecordType && (
				<Grid item lg={2} md={3} xs={11}>
					<FormControl fullWidth size="small">
						<InputLabel>Record Type</InputLabel>
						<Select
						value={selectedRecordType}
						onChange={handleRecordTypeChange}
						label="Record Type"
						>
							<MenuItem value={RecordTypeEnum.REAL}>Real</MenuItem>
							<MenuItem value={RecordTypeEnum.TEST}>Test</MenuItem>
							<MenuItem value={RecordTypeEnum.ALL}>All</MenuItem>
						</Select>
					</FormControl>
				</Grid>)
				}
				<Grid item lg={3} md={4} xs={12}>
					<CustomDatePicker
						label="Start Date"
						value={startDate}
						onChange={(values) => handleFilterChange('startDate', values)}
						disableFuture={true}
					/>
				</Grid>
				<Grid item lg={3} md={4} xs={12}>
					<CustomDatePicker
						label="End Date"
						value={endDate}
						minDate={minEndDate}
						onChange={(values) => handleFilterChange('endDate', values)}
					/>
				</Grid>
				<Grid item lg={4} md={4} xs={12}>
					<Autocomplete
						options={BOOLEANOPTIONS}
						value={filters.false_positive===true? "True": filters.false_positive===false?"False": "All"}
						onChange={(event, newValue) => {
						handleFilterChange('false_positive', (newValue && newValue === "True")? true:  (newValue && newValue === "False")? false: null)						
						}}
						disableClearable
						renderInput={(params) => (
						<TextField
							{...params}
							label="False Positive"
						/>
						)}
						fullWidth
						isOptionEqualToValue={(option, value) => option === value}
					/>
				</Grid>
				<Grid item lg={4} md={0} xs={0}/>
				<Grid item lg={4} md={0} xs={0}/>
			</Grid>
		<Divider sx={{mt: 5, mb: 5}}/>
		<Stack 
			direction="row" 
			spacing={2} 
			justifyContent="flex-end" 
			alignItems="center"
			sx={{
				flexWrap: 'wrap',
				gap: 2,
			}}
		>
			<Button
				variant="contained"
				color="primary"
				onClick={() => handleRefetch()}
				sx={{minWidth: '150px', height: '42px'}}
			>
				Search
			</Button>
			<Button
				variant="outlined"
				color="secondary"
				onClick={handleExport}
				sx={{minWidth: '150px'}}
			>
				Export
			</Button>
			<RetriggerScansPopup/>
			<RetriggerRemovalsPopup/>
			<RetriggerInviteEmail />
			<AddBrokerRecordPopup/>
		</Stack>
		<Divider sx={{mt: 6, mb: 6}}/>
		</Container>
	)
}

export default memo(FilterContainer)
